import React, { useCallback, useEffect, useMemo, useRef, useState, Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button, Dropdown, Input, Modal, Spin, Typography } from "antd";
import { MoreOutlined, LockOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { isStaffEmail } from "@utils/user_utils";
import { usePermissionCheck, PermissionViewable, useUserPermissions } from "@components/PermissionedComponents";
import { Navbar } from "../Navigation/Navbar";
import { DashboardProvider } from "./Contexts/DashboardContext";
import {
  useControllerDependents, useControllers, useDashboard, useSlicerVariables,
} from "./Contexts/dashboardHooks";
import { useControllerHover } from "./Contexts/ControllerHoverContext";
import { CONTROLLER_TYPE, controllerBareId, sliceColor, widgetLabels } from "./Processing/controllerBindings";
import type { WidgetEntry } from "./Contexts/dashboardState";
import { ChartSyncProvider } from "./Contexts/ChartSyncContext";
import { ReactGridShell } from "./Components/ReactGridShell";
import { defaultWidgetRegistry, createWidgetRenderer } from "./WidgetComponents/widgetRenderer";
import { WidgetErrorBoundary } from "./WidgetComponents/WidgetErrorBoundary";
import { ConnectedSlicerBadges } from "./WidgetComponents/ConnectedSlicerBadges";
import { AddWidgetModal } from "./AddWidgetModal/AddWidgetModal";
import { PageBreakOverlay } from "./Components/PageBreakOverlay";
import { GRID_MAX_WIDTH_PX } from "./PDF/pdfLayout";
import { DashboardToolbar } from "./Components/DashboardToolbar";
import { buildWidgetMenuItems } from "./Components/widgetMenuItems";
import { confirmDeleteWidget, refuseControllerDelete } from "./Components/dashboardConfirms";
import { EmptyDashboardMenu } from "./Components/EmptyDashboardMenu";
import { SandboxTutorial, hasSeenTutorial } from "./Components/SandboxTutorial";
import { useExperimentalFlag } from "./Components/experimentalFlags";
import { SandboxAIRoot } from "./SandboxAI";
import { SANDBOX_AI_STAFF_ONLY } from "./SandboxAI/config";
import "./DashboardAlt.css";

const widgetRenderer = createWidgetRenderer(defaultWidgetRegistry);

/**
 * The tile body. It reads the slicer hover state itself, so a hover re-renders only the
 * frames: the widget elements inside come from `renderItem`, stay referentially stable,
 * and React skips them. A frame is outlined while the pointer is on a slicer it follows.
 */
function GridItemFrame({ id, children }: { id: string; children: ReactNode }) {
  const { hoveredUuid } = useControllerHover();
  const dependents = useControllerDependents();
  const controllers = useControllers();
  const linked = hoveredUuid != null && !!dependents[hoveredUuid]?.all.has(id);
  const hovered = linked ? controllers.find((c) => c.uuid === hoveredUuid) : undefined;
  // The outline takes the hovered slicer's color through a CSS variable.
  const style = hovered
    ? ({ "--slicer-color": sliceColor(hovered.spec.slicer_builder_misc) } as React.CSSProperties)
    : undefined;
  return (
    <div className={`grid-item-content${linked ? " is-slicer-linked" : ""}`} style={style}>
      {children}
      <ConnectedSlicerBadges id={id} />
    </div>
  );
}

class DashboardErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", flexDirection: "column", gap: 12 }}>
          <Typography.Title level={4}>Something went wrong</Typography.Title>
          {/* Reload, not a state reset: re-rendering the tree that just threw hits the
              same error, and for a stale lazy chunk only a fresh index.html can recover. */}
          <Button onClick={() => window.location.reload()}>Try again</Button>
        </div>
      );
    }
    return this.props.children;
  }
}

// PdfExportControl now lives in PDF/PdfExportControl.tsx. It is rendered inside
// ChartSyncProvider (below) so useChartSync() is available.

function DashboardContent() {
  const {
    state, renameDashboard,
    duplicateWidget, removeWidget, onLayoutChange,
  } = useDashboard();
  const cleanView = useExperimentalFlag("cleanView");
  const controllerDependents = useControllerDependents();
  const slicerValues = useSlicerVariables();
  const gridRef = useRef<HTMLDivElement>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<WidgetEntry | null>(null);

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const { permissionDisabled: cannotEdit } = usePermissionCheck("EditSandboxBoards");

  const { user } = useAuth0();
  const dvaiEnabled = useSelector(({ team }: any) => team?.info?.dvai_enabled);
  const sandboxAIEnabled = useSelector(
    ({ team }: any) => team?.info?.sandbox_ai_enabled,
  );
  const sandboxAIGate = SANDBOX_AI_STAFF_ONLY
    ? isStaffEmail(user?.email) || !!sandboxAIEnabled
    : isStaffEmail(user?.email) || !!sandboxAIEnabled || !!dvaiEnabled;
  const showSandboxAI =
    !cannotEdit && !!state.dashboardId && !state.error && sandboxAIGate;

  // First visit in this browser: open the tutorial once the dashboard has loaded.
  useEffect(() => {
    if (state.dashboardId && !state.error && !hasSeenTutorial()) setTutorialOpen(true);
  }, [state.dashboardId, state.error]);

  const teamColors = useSelector(
    ({ team }: any) => team?.info?.team_colors,
  );
  const gutterStyle = useMemo(() => ({
    "--gutter-left-color": teamColors?.primary || "#d9d9d9",
    "--gutter-right-color": teamColors?.secondary || "#d9d9d9",
  }) as React.CSSProperties, [teamColors?.primary, teamColors?.secondary]);

  const handleEdit = useCallback((widget: WidgetEntry) => {
    setEditingWidget(widget);
    setModalOpen(true);
  }, []);

  // Deleting a controller other widgets follow would silently change their behavior, so
  // it is refused until every binding is removed in the widget that owns it.
  const handleDelete = useCallback(
    (widget: WidgetEntry) => {
      if (widget.type === CONTROLLER_TYPE) {
        const governed = controllerDependents[controllerBareId(widget.id)]?.all;
        if (governed?.size) {
          refuseControllerDelete(widgetLabels(governed, state.widgets, slicerValues));
          return;
        }
      }
      confirmDeleteWidget(() => removeWidget(widget.id));
    },
    [removeWidget, controllerDependents, state.widgets, slicerValues],
  );

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setEditingWidget(null);
  }, []);

  const handleAddNew = useCallback(() => {
    setEditingWidget(null);
    setModalOpen(true);
  }, []);

  const openRenameModal = useCallback(() => {
    setRenameValue(state.name);
    setRenameOpen(true);
  }, [state.name]);

  const handleRename = useCallback(async () => {
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    await renameDashboard(trimmed);
    setRenameOpen(false);
  }, [renameValue, renameDashboard]);

  const widgets = state.widgets;

  const renderItem = useCallback((id: string) => {
    const widget = widgets[id];
    if (!widget) return <div>{id}</div>;

    const menuItems = buildWidgetMenuItems(widget, {
      disabled: cannotEdit,
      onEdit: handleEdit,
      onDuplicate: duplicateWidget,
      onDelete: handleDelete,
    });

    return (
      <GridItemFrame id={id}>
        <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
          <button
            className="grid-item-menu"
            onClick={(e) => e.stopPropagation()}
            title="Widget options"
          >
            <MoreOutlined />
          </button>
        </Dropdown>
        <WidgetErrorBoundary widgetId={id}>
          {widgetRenderer(id, widget.type)}
        </WidgetErrorBoundary>
      </GridItemFrame>
    );
  }, [widgets, handleEdit, handleDelete, duplicateWidget]);

  if (!state.dashboardId) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Spin size="large" tip={state.error ? "Failed to load dashboard" : "Loading dashboard…"} />
      </div>
    );
  }

  const isEmptyDashboard = Object.keys(widgets).length === 0;

  return (
    <ChartSyncProvider containerRef={gridRef}>
    <div
      className={`dashboard-grid-scroll-wrapper${cleanView ? " dashalt-clean-view" : ""}`}
      style={gutterStyle}
    >
      <DashboardToolbar
        onRenameDashboard={openRenameModal}
        onAddWidget={handleAddNew}
        onShowTutorial={() => setTutorialOpen(true)}
      />

      {isEmptyDashboard ? (
        <EmptyDashboardMenu onAddWidget={handleAddNew} />
      ) : (
        <div ref={gridRef} className="dashboard-grid-container" style={{ width: GRID_MAX_WIDTH_PX }}>
          <ReactGridShell
            layout={state.layout}
            onLayoutChange={onLayoutChange}
            renderItem={renderItem}
            isDraggable={!cannotEdit}
            isResizable={!cannotEdit}
          />
          <PageBreakOverlay containerRef={gridRef} layout={state.layout} />
        </div>
      )}
    </div>

      <AddWidgetModal
        open={modalOpen}
        onClose={handleModalClose}
        existingWidget={editingWidget ?? undefined}
      />

      {showSandboxAI && <SandboxAIRoot />}

      <SandboxTutorial isOpen={tutorialOpen} setOpen={setTutorialOpen} showSandboxAI={showSandboxAI} />

      <Modal
        open={renameOpen}
        title="Rename Dashboard"
        onOk={handleRename}
        onCancel={() => setRenameOpen(false)}
        okText="Save"
        destroyOnClose
      >
        <Input
          autoFocus
          placeholder="Dashboard name"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onPressEnter={handleRename}
        />
      </Modal>
    </ChartSyncProvider>
  );
}

const DashboardAltPage: React.FC = () => {
  const permissionsManagerList: { first_name: string; last_name: string }[] = useSelector(
    ({ team }: any) => team?.info?.permissions_manager_list ?? [],
  );
  const userPermissions = useUserPermissions();
  const { permissionDisabled: cannotEdit } = usePermissionCheck("EditSandboxBoards");

  const noViewPermissionMessage = (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 12, textAlign: "center" }}>
      <LockOutlined style={{ fontSize: 40, color: "#bfbfbf" }} />
      <Typography.Title level={4} style={{ margin: 0 }}>Sandbox access required</Typography.Title>
      <Typography.Text type="secondary">
        You do not have permission to view the Sandbox.<br />
        Please reach out to your team's permissions manager to request access.
      </Typography.Text>
      {permissionsManagerList.length > 0 && (
        <Typography.Text>
          Permissions Manager{permissionsManagerList.length > 1 ? "s" : ""}:{" "}
          <strong>{permissionsManagerList.map((u) => `${u.first_name} ${u.last_name}`).join(", ")}</strong>
        </Typography.Text>
      )}
    </div>
  );

  return (
    <div style={{ background: "#f5f5f5", minHeight: "100vh" }}>
      <Navbar />
      {userPermissions != null && (
        <PermissionViewable permissionCheck="ViewSandboxBoards" permissionMessage={noViewPermissionMessage} tooltipMessage="You don't have permission to view the Sandbox">
          <DashboardProvider canEdit={!cannotEdit}>
            <DashboardErrorBoundary>
              <DashboardContent />
            </DashboardErrorBoundary>
          </DashboardProvider>
        </PermissionViewable>
      )}
    </div>
  );
};

export default DashboardAltPage;
